import { useCallback, useState } from "react";
import { ConnectProto } from "@racoon/api";
import { useSelectedCompany } from "~/shared/store/company/hooks/useSelectedCompany";
import { useLazyGetUserQuery } from "~/shared/api/user/user.grpc";
import { UserFormDialog } from "~/features/user-form/ui/user-form.dialog";

/**
 * Opens an employee's card from a page that only mentions them — a job's
 * driver, a route's driver — without leaving it for the Employees registry.
 *
 * The caller renders `userCard` once.
 */
export function useUserCard() {
  const { selectedCompanyId } = useSelectedCompany();
  const [user, setUser] = useState<ConnectProto.User | null>(null);
  const [open, setOpen] = useState(false);

  const [getUser] = useLazyGetUserQuery();

  const openUser = useCallback((next: ConnectProto.User) => {
    setUser(next);
    setOpen(true);
  }, []);

  /** For rows that carry only the id — a shift names its driver, not the record. */
  const openUserById = useCallback(
    async (id: bigint | number | string) => {
      if (!selectedCompanyId) return;
      try {
        const fetched = await getUser(
          { id: BigInt(id), ownerOrganizationId: BigInt(selectedCompanyId) },
          true,
        ).unwrap();
        openUser(fetched);
      } catch {
        // The global error toaster reports the failure; the page stays as it is.
      }
    },
    [selectedCompanyId, getUser, openUser],
  );

  const userCard = (
    <UserFormDialog
      open={open}
      onOpenChange={(next: boolean) => {
        setOpen(next);
        if (!next) setUser(null);
      }}
      user={user}
      org={{ mode: "fixed", id: selectedCompanyId }}
      showGroups
    />
  );

  return { openUser, openUserById, userCard };
}
